import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { gaEmailCaptureSubmitted } from '../lib/gaPixel';

/**
 * Email-capture bottom sheet (tranche 2 of the OTP-auth spec).
 *
 * Fires once the board is ready + the user has dwelled (see
 * `useEmailCaptureTrigger`) and offers a SOFT save — "don't lose {name}'s
 * board, save it to your email." One field, one clay button, no code. Capture
 * is not verification: the address is stored unverified and a re-engagement
 * email (carrying a resume-token board link) is the come-back path. On submit
 * the same sheet flips to a "Saved / Sent to {email}" confirmation
 * IMMEDIATELY (optimistic) and auto-dismisses — the `onSubmit` capture runs in
 * the background so the user never waits on the callable's cold-start + Klaviyo
 * round-trip. The host wires `onSubmit` to a keepalive beacon so the write
 * survives the user leaving; capture is best-effort, so a failure just means no
 * email was stored (the user still saw "Saved").
 *
 * Built on the same portal + scrim pattern as the board's other dismissible
 * sheets (`SavePickerSheet`, `AboutSheet`) rather than the always-open vaul
 * saved tray — a triggered, dismissible sheet wants tap-outside + swipe-down
 * dismissal, which this pattern gives cleanly (and, unlike vaul, it renders +
 * asserts reliably in jsdom and stays Happo-deterministic). There is NO
 * explicit close button by design (research + mock): the dismiss affordances
 * are the scrim tap, a downward swipe, and Escape.
 *
 * Presentational: the host owns WHEN it opens, the capture callable, and the
 * dismiss/captured bookkeeping (snooze, caps, localStorage). This component
 * owns only the field, validation, and the two visual states — so it (and its
 * story) never touch Firebase.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_CONFIRMATION_MS = 2400;

// Downward drag (px) past which a swipe on the sheet dismisses it.
const SWIPE_DISMISS_PX = 60;

// ─── Styles ─────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 70;
  background: rgba(28, 22, 17, 0.42);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  animation: emailCaptureFade 180ms ease both;
  @keyframes emailCaptureFade {
    from { opacity: 0; }
    to { opacity: 1; }
  }
  @media (min-width: 640px) {
    align-items: center;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 440px;
  background: ${({ theme }) => theme.color.creamLight};
  border-top-left-radius: 26px;
  border-top-right-radius: 26px;
  box-shadow: 0 -8px 32px rgba(80, 40, 20, 0.18);
  padding: 12px 24px calc(28px + env(safe-area-inset-bottom));
  animation: emailCaptureUp 220ms var(--ease-settle) both;
  @keyframes emailCaptureUp {
    from { transform: translateY(20px); opacity: 0.7; }
    to { transform: translateY(0); opacity: 1; }
  }
  @media (min-width: 640px) {
    border-radius: 26px;
    box-shadow: 0 24px 48px rgba(80, 40, 20, 0.22);
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Grabber = styled.div`
  width: 40px;
  height: 4px;
  border-radius: 3px;
  background: ${({ theme }) => theme.color.warmBorder};
  margin: 0 auto 20px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 21px;
  font-weight: 600;
  line-height: 1.32;
  letter-spacing: -0.01em;
  color: hsl(var(--foreground));
`;

const Subline = styled.p`
  margin: 9px 0 19px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 15px;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 52px;
  padding: 0 14px;
  margin-bottom: 12px;
  border: 1.5px solid ${({ theme }) => theme.color.warmBorder};
  border-radius: 14px;
  background: ${({ theme }) => theme.color.cream};
  &:focus-within {
    border-color: hsl(var(--primary));
  }
`;

const FieldIcon = styled.span`
  font-size: 17px;
  line-height: 1;
  flex: 0 0 auto;
`;

const EmailInput = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 16px;
  color: hsl(var(--foreground));
  &::placeholder {
    color: ${({ theme }) => theme.color.mutedForeground};
    opacity: 0.7;
  }
  /* The bare-input reset above kills the UA focus ring, and the Field's
     :focus-within border tint alone isn't a WCAG 2.4.7 focus indicator for
     keyboard users. Restore a real ring — same outline convention as the
     board/guide/saved controls. Text inputs match :focus-visible on any
     focus, so it shows for pointer and keyboard alike; the rounded corners
     echo the field pill instead of a hard rectangle. */
  &:focus-visible {
    outline: 2px solid hsl(var(--primary));
    outline-offset: 2px;
    border-radius: 8px;
  }
`;

const NotNowButton = styled.button`
  display: block;
  margin: 10px auto 0;
  padding: 6px 8px;
  background: none;
  border: 0;
  font: inherit;
  font-size: 13px;
  color: ${({ theme }) => theme.color.mutedText};
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 28px;
  padding: 16px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 16px;
  font-weight: 500;
  color: #ffffff;
  background: hsl(var(--primary));
  cursor: pointer;
  &:hover {
    background: ${({ theme }) => theme.color.clayPressed};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ConfirmTitle = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 21px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: hsl(var(--foreground));
`;

const ConfirmSub = styled.p`
  margin: 9px 0 4px;
  font-family: ${({ theme }) => theme.font.sans};
  font-size: 15px;
  line-height: 1.45;
  color: hsl(var(--muted-foreground));
`;

// ─── Component ──────────────────────────────────────────────────────

export interface EmailCaptureSheetProps {
  open: boolean;
  /** The recipient's DISPLAY name (typed value, e.g. "Grandma") — NOT the
   *  relationship enum. Headline wraps to two lines for long names.
   *
   *  Nullable on purpose. The two account-surface hosts open this sheet without
   *  a board in context, and both used to paper over it with the literal string
   *  'your' — which rendered "Don't lose your's board". Pass null/undefined and
   *  get a clean plural headline instead. */
  recipientName?: string | null;
  /** Persist the address. Resolves on success, rejects to keep the sheet open
   *  with an error. The host runs the `captureEmail` callable here. */
  onSubmit: (email: string) => Promise<void>;
  /** User closed the sheet WITHOUT capturing (swipe-down / scrim tap / Esc).
   *  The host snoozes + fires the dismissed GA event. */
  onDismiss: () => void;
  /** Capture succeeded and the confirmation has been shown — the host closes
   *  the sheet + records the capture (no snooze, no dismissed event). */
  onCaptured: () => void;
  /** Confirmation dwell before auto-dismiss. `null` disables the timer (stories
   *  / tests that want to inspect the settled confirmation state). */
  confirmationMs?: number | null;
  /** Story/test affordance: mount straight into the "Saved / Sent to …"
   *  confirmation state for this address (mirrors `SaveToast`'s story-only
   *  `autoDismissMs`). Undefined in production — the sheet reaches confirmation
   *  only via a real submit. Pair with `confirmationMs={null}` to freeze it. */
  initialConfirmedEmail?: string;
  /** GA `surface` value for the submitted event — 'board' when the sheet is
   *  triggered on the board (default), 'account' when reopened from the
   *  persistent "Save your boards" affordance. Lets the two entry points be
   *  told apart in the opt-in funnel without spending a new dimension slot. */
  surface?: string;
}

export const EmailCaptureSheet: React.FC<EmailCaptureSheetProps> = ({
  open,
  recipientName,
  onSubmit,
  onDismiss,
  onCaptured,
  confirmationMs = DEFAULT_CONFIRMATION_MS,
  initialConfirmedEmail,
  surface = 'board',
}) => {
  const [email, setEmail] = useState('');
  // Once true the sheet shows the confirmation state; a dismiss gesture here
  // means "done", not "dismissed".
  const [confirmed, setConfirmed] = useState(Boolean(initialConfirmedEmail));
  const confirmedRef = useRef(Boolean(initialConfirmedEmail));
  const submittedEmailRef = useRef(initialConfirmedEmail ?? '');
  const touchStartYRef = useRef<number | null>(null);
  // Skip the reopen-reset on the very first mount so a story/test can force the
  // confirmation state; genuine re-opens (a later show in the same session)
  // still reset to the form.
  const firstMountRef = useRef(true);
  // Latest close callbacks ride refs so the auto-dismiss timer / listeners arm
  // exactly once and never against a stale closure.
  const onCapturedRef = useRef(onCaptured);
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onCapturedRef.current = onCaptured;
    onDismissRef.current = onDismiss;
  });

  // Reset transient state whenever the sheet re-opens (a later show in the
  // same session must start on the form, not a stale confirmation).
  useEffect(() => {
    if (firstMountRef.current) {
      firstMountRef.current = false;
      return;
    }
    if (open) {
      setConfirmed(false);
      confirmedRef.current = false;
      setEmail('');
    }
  }, [open]);

  // A dismiss gesture (scrim tap / swipe / Escape). Routes to the right close
  // path depending on whether we've already captured.
  const dismiss = () => {
    if (confirmedRef.current) onCapturedRef.current();
    else onDismissRef.current();
  };

  // Escape closes the sheet while open (capture-phase + stopPropagation so a
  // sheet stacked over the board PDP doesn't also dismiss the layer beneath —
  // mirrors SavePickerSheet).
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        dismiss();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Confirmation dwell → auto-dismiss.
  useEffect(() => {
    if (!confirmed || confirmationMs === null) return undefined;
    const t = window.setTimeout(() => onCapturedRef.current(), confirmationMs);
    return () => window.clearTimeout(t);
  }, [confirmed, confirmationMs]);

  const valid = EMAIL_RE.test(email.trim());

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_RE.test(value) || confirmedRef.current) return;
    // Optimistic: flip to the confirmation IMMEDIATELY and run the capture in
    // the background. The host's onSubmit is a keepalive beacon
    // (`captureEmailBeacon`), so the write survives the user leaving — and they
    // never wait on the callable's cold-start + synchronous Klaviyo forward
    // (the old `await onSubmit` was the multi-second "Saving…" hang). Capture is
    // a best-effort soft nudge, so a background rejection is swallowed: the user
    // has already seen "Saved".
    submittedEmailRef.current = value;
    gaEmailCaptureSubmitted({ surface });
    confirmedRef.current = true;
    setConfirmed(true);
    void Promise.resolve()
      .then(() => onSubmit(value))
      .catch(() => undefined);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0]?.clientY ?? null;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    const start = touchStartYRef.current;
    touchStartYRef.current = null;
    if (start === null) return;
    const end = e.changedTouches[0]?.clientY ?? start;
    if (end - start > SWIPE_DISMISS_PX) dismiss();
  };

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <Overlay data-testid="email-capture-scrim" onClick={dismiss}>
      <Sheet
        role="dialog"
        aria-modal="true"
        aria-label="Save your board"
        data-testid="email-capture-sheet"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <Grabber aria-hidden />
        {confirmed ? (
          <div>
            <ConfirmTitle>Saved</ConfirmTitle>
            <ConfirmSub>Sent to {submittedEmailRef.current}</ConfirmSub>
          </div>
        ) : (
          <>
            <Title>
              {recipientName
                ? `Don\u2019t lose ${recipientName}\u2019s board`
                : 'Don\u2019t lose your boards'}
            </Title>
            <Subline>Save it to your email and come back anytime.</Subline>
            <Form onSubmit={handleSubmit} noValidate>
              <Field>
                <FieldIcon aria-hidden>✉️</FieldIcon>
                <EmailInput
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="you@email.com"
                  aria-label="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </Field>
              <SubmitButton type="submit" disabled={!valid}>
                Save my board
              </SubmitButton>
            </Form>
            {/* Dismissal should be a decision, not an accident. Every other
                close path (scrim, swipe, Esc) is also what you'd try in order
                to get BACK to the board — and each one costs a snooze. */}
            <NotNowButton type="button" onClick={onDismiss}>
              Not now
            </NotNowButton>
          </>
        )}
      </Sheet>
    </Overlay>,
    document.body,
  );
};

export default EmailCaptureSheet;
